import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BrowserFrame } from '../components/BrowserFrame';
import BrandLogo from '../components/BrandLogo';
import { SCREENSHOTS } from '../lib/screenshots';
import { PLANS } from '../plans';
import { LandmarkIcon, LightbulbIcon, PauseIcon, PlayIcon, ShieldCheckIcon, TargetIcon } from '../components/icons';

/* Logo click: already home → smooth-scroll to top (instant when reduced motion). */
function scrollToTop() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  window.scrollTo({ top: 0, behavior: reduced ? 'auto' : 'smooth' });
}

/* Scroll-reveal micro-animation (respects prefers-reduced-motion via CSS). */
function Reveal({ children, delay = '', className = '' }: { children: React.ReactNode; delay?: '' | 'reveal-d1' | 'reveal-d2' | 'reveal-d3'; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Safety fallback: if IntersectionObserver is unavailable or never
    // fires, reveal anyway so content is never stuck invisible.
    const fallback = window.setTimeout(() => el.classList.add('in'), 1500);
    if (typeof IntersectionObserver === 'undefined') return () => window.clearTimeout(fallback);
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('in'); io.disconnect(); } }),
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => {
      window.clearTimeout(fallback);
      io.disconnect();
    };
  }, []);
  return <div ref={ref} className={`reveal ${delay} ${className}`}>{children}</div>;
}

function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

/* ---------------- hero (full-bleed rotating background videos) ---------------- */

const HERO_VIDEOS = [
  { src: '/media/hero-bg.mp4', poster: '/media/hero-poster.jpg' },
  { src: '/media/hero-rfp.mp4', poster: '/media/hero-rfp-poster.jpg' },
];
const HERO_ROTATE_MS = 10000;

/**
 * R2-BR-D19 / RC-2026-10-26.8 (owner decision 1) — RESTORE POINT.
 * SAM.gov is not provisioned yet, so no feed record carries set-aside
 * data and the set-aside filter cannot return results. Until the
 * owner's SAM key is configured AND the feed + set-aside journey is
 * verified end to end, the landing claim stays narrowed to what the
 * product can actually do. When (and only when) that verification
 * passes, restore the original claim in this ONE place:
 *   'Filter by agency, value, deadline, set-aside, and eligibility'
 */
const SET_ASIDE_FILTER_CLAIM = 'Filter by agency, value, deadline, and eligibility';

function Hero() {
  const reduced = useReducedMotion();
  const [failed, setFailed] = useState<number[]>([]);
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const videoRefs = useRef<Array<HTMLVideoElement | null>>([]);
  const failedRef = useRef<number[]>([]);
  useEffect(() => {
    failedRef.current = failed;
  }, [failed]);

  const markFailed = (i: number) => {
    setFailed((prev) => {
      if (prev.includes(i)) return prev;
      const next = [...prev, i];
      setActive((cur) => {
        if (cur !== i) return cur;
        const fallback = HERO_VIDEOS.findIndex((_, j) => j !== i && !next.includes(j));
        return fallback >= 0 ? fallback : 0;
      });
      return next;
    });
  };

  const playableCount = HERO_VIDEOS.length - failed.length;

  /* Rotate between the hero videos on a timer; skip when reduced motion is on,
     the user paused the video, or fewer than two videos are playable. */
  useEffect(() => {
    if (reduced || paused || playableCount < 2) return;
    const id = window.setInterval(() => {
      setActive((cur) => {
        for (let k = 1; k <= HERO_VIDEOS.length; k++) {
          const n = (cur + k) % HERO_VIDEOS.length;
          if (!failedRef.current.includes(n)) return n;
        }
        return cur;
      });
    }, HERO_ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reduced, paused, playableCount]);

  /* Play the visible video, pause the hidden ones to save CPU/battery. */
  useEffect(() => {
    videoRefs.current.forEach((v, i) => {
      if (!v) return;
      if (i === active && !failed.includes(i) && !reduced && !paused) {
        v.play().catch(() => {});
      } else {
        v.pause();
      }
    });
  }, [active, failed, reduced, paused]);

  const activePoster = HERO_VIDEOS[active] ? HERO_VIDEOS[active].poster : '/media/hero-poster.jpg';

  return (
    <section className="lp-hero" aria-label="Grant and RFP Navigator introduction">
      {/* Poster sits underneath the videos so a failed/slow load still looks intentional. */}
      <div className="lp-hero-media" aria-hidden="true">
        <img src={activePoster} alt="" className="lp-hero-poster" />
        {!reduced &&
          HERO_VIDEOS.map((v, i) =>
            failed.includes(i) ? null : (
              <video
                key={v.src}
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                className={`lp-hero-video${i === active ? ' is-active' : ''}`}
                muted
                loop
                playsInline
                preload="auto"
                poster={v.poster}
                onError={() => markFailed(i)}
              >
                <source src={v.src} type="video/mp4" onError={() => markFailed(i)} />
              </video>
            ),
          )}
      </div>
      <div className="lp-hero-overlay" aria-hidden="true" />
      <div className="container lp-hero-content">
        <Reveal>
          <span className="lp-eyebrow">Funding intelligence for ambitious teams</span>
          <h1 className="lp-h1">
            Find the right funding. <span className="lp-accent">Build the strongest response.</span>
          </h1>
          <p className="lp-lede">
            Discover grants and RFPs matched to your organization, understand why they fit,
            and move every submission from research to deadline in one focused workspace.
          </p>
          <div className="mkt-cta-row">
            <Link to="/app/onboarding?path=nonprofit" className="btn btn-lg lp-btn-light">Get grant ready</Link>
            <Link to="/app/onboarding?path=smallbiz" className="btn btn-lg lp-btn-outline">Get contract ready</Link>
            <Link to="/signup?kind=individual" className="btn btn-lg lp-btn-outline">Start personal funding</Link>
          </div>
          <div className="lp-tertiary-row">
            <a href="#how" className="lp-tertiary">See how it works ↓</a>
            <a href="#new-here" className="lp-tertiary">Never written a grant before? Start here ↓</a>
          </div>
        </Reveal>
      </div>
      {/* WCAG 2.2.2: ambient background video must be pausable. */}
      {!reduced && playableCount > 0 && (
        <button
          type="button"
          className="lp-hero-media-toggle"
          onClick={() => setPaused((p) => !p)}
          aria-pressed={paused}
          aria-label={paused ? 'Play background video' : 'Pause background video'}
          title={paused ? 'Play background video' : 'Pause background video'}
        >
          <span aria-hidden="true" style={{ display: 'inline-flex' }}>{paused ? <PlayIcon size={14} /> : <PauseIcon size={14} />}</span>
        </button>
      )}
    </section>
  );
}

/* ---------------- new here: grants in plain language ---------------- */

const NEW_HERE_CARDS = [
  {
    title: 'What a grant actually is',
    text: 'Money awarded to do specific work — run a program, complete a project, fund research. You don’t pay it back, but you do report on what it achieved.',
  },
  {
    title: 'Who gives them out',
    text: 'Government agencies, foundations, and companies publish funding opportunities with rules, deadlines, and application forms. Thousands are open at any given time.',
  },
  {
    title: 'What it takes to win one',
    text: 'First eligibility, then a written proposal: what you’ll do, what it costs, and why you’re the right team. It’s competitive — but very winnable with preparation.',
  },
];

const NEW_HERE_ICONS = [LightbulbIcon, LandmarkIcon, TargetIcon];

function NewHere() {
  return (
    <section className="mkt-section" id="new-here" aria-labelledby="new-here-h">
      <div className="container">
        <Reveal>
          <span className="lp-kicker">New here?</span>
          <h2 className="mkt-h2" id="new-here-h">Grants, in plain language</h2>
          <p className="mkt-sub">
            Never applied for funding before? Here’s the two-minute version. No jargon, no assumptions —
            and everything below is built into the platform to guide you.
          </p>
        </Reveal>
        <div className="mkt-grid-3">
          {NEW_HERE_CARDS.map((c, i) => {
            const CardIcon = NEW_HERE_ICONS[i];
            return (
            <Reveal key={c.title} delay={i === 1 ? 'reveal-d1' : i === 2 ? 'reveal-d2' : ''}>
              <div className="card mkt-feat">
                <div className="mkt-feat-ico" aria-hidden="true"><CardIcon size={22} /></div>
                <h3>{c.title}</h3>
                <p>{c.text}</p>
              </div>
            </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- audience tabs ---------------- */

interface Audience {
  id: string;
  label: string;
  headline: string;
  body: string;
  bullets: string[];
  ctaTo: string;
  ctaText: string;
}

const AUDIENCES: Audience[] = [
  {
    id: 'nonprofits',
    label: 'Nonprofits',
    headline: 'Turn your mission into funded programs.',
    body: 'Find grants matched to your cause, understand exactly why each one fits, and build proposals with guided help — even if no one on your team has written one before.',
    bullets: [
      'Match scores explained in plain language, with cited reasons',
      'Readiness checklists that surface gaps weeks before deadlines',
      'A proposal workspace with drafting help and budget tools',
    ],
    ctaTo: '/app/onboarding?path=nonprofit',
    ctaText: 'Get grant ready',
  },
  {
    id: 'smallbiz',
    label: 'Small businesses & startups',
    headline: 'Compete for contracts like a bigger company.',
    body: 'Track RFPs matched to what you sell, check your readiness against real contracting requirements, and assemble compliant responses — without hiring a capture team.',
    bullets: [
      'RFP discovery filtered to your capabilities and NAICS codes',
      'GovCon readiness checklists modeled on real requirements',
      'Pipeline and proposal tools that keep every deadline visible',
    ],
    ctaTo: '/app/onboarding?path=smallbiz',
    ctaText: 'Get contract ready',
  },
  {
    id: 'individuals',
    label: 'Individuals',
    headline: 'Funding for school — and help when life gets expensive.',
    body: 'One $399/year personal workspace for scholarships, grants, and fellowships matched to you — plus honest guidance on the assistance programs that help with bills, rent, and utilities when money is tight. We’ll always tell you what’s real and what’s a myth: nobody here will promise you \u201cfree money.\u201d',
    bullets: [
      'Scholarships and fellowships matched to your profile',
      'Plain-language guidance on benefit programs like SNAP and LIHEAP',
      'Local aid pointers — like dialing 211 — with application tracking',
      'Scam warnings built in: real aid is always free to apply for',
    ],
    ctaTo: '/signup?kind=individual',
    ctaText: 'Start personal funding',
  },
];

function AudienceTabs() {
  const [active, setActive] = useState(0);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const onKeyDown = (e: React.KeyboardEvent, index: number) => {
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (index + 1) % AUDIENCES.length;
    else if (e.key === 'ArrowLeft') next = (index - 1 + AUDIENCES.length) % AUDIENCES.length;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = AUDIENCES.length - 1;
    if (next !== null) {
      e.preventDefault();
      setActive(next);
      tabRefs.current[next]?.focus();
    }
  };

  const audience = AUDIENCES[active];

  return (
    <section className="mkt-section alt" id="who" aria-labelledby="who-h">
      <div className="container">
        <Reveal>
          <span className="lp-kicker">Who it’s for</span>
          <h2 className="mkt-h2" id="who-h">Built for your kind of funding journey</h2>
          <p className="mkt-sub">
            A brand-new nonprofit, a growing business chasing contracts, or one person funding their future —
            pick your path and the platform shapes itself around you.
          </p>
        </Reveal>
        <Reveal delay="reveal-d1">
          <div className="who-merged">
            <div className="who-merged-media">
              <div className="promo-video-wrap">
                <video
                  className="promo-video"
                  controls
                  playsInline
                  preload="metadata"
                  poster="/promo/navigator-promo-poster.jpg"
                  aria-label="Promo video: what the Grant and RFP Navigator does for you, in about fifty seconds"
                >
                  <source src="/promo/navigator-promo.mp4" type="video/mp4" />
                  Sorry, your browser can’t play this video.
                </video>
                <span className="promo-badge" aria-hidden="true">0:50</span>
              </div>
              <p className="who-video-caption">Watch: your funding journey in 50 seconds</p>
            </div>
            <div className="who-merged-content">
              <div className="aud-tabs" role="tablist" aria-label="Choose your audience">
                {AUDIENCES.map((a, i) => (
                  <button
                    key={a.id}
                    ref={(el) => { tabRefs.current[i] = el; }}
                    role="tab"
                    id={`aud-tab-${a.id}`}
                    aria-selected={active === i}
                    aria-controls={`aud-panel-${a.id}`}
                    tabIndex={active === i ? 0 : -1}
                    className={`aud-tab${active === i ? ' active' : ''}`}
                    onClick={() => setActive(i)}
                    onKeyDown={(e) => onKeyDown(e, i)}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
              <div
                role="tabpanel"
                id={`aud-panel-${audience.id}`}
                aria-labelledby={`aud-tab-${audience.id}`}
                className="who-panel"
              >
                <h3>{audience.headline}</h3>
                <p className="aud-body">{audience.body}</p>
                <ul className="check-list">
                  {audience.bullets.map((b) => <li key={b}>{b}</li>)}
                </ul>
                <Link to={audience.ctaTo} className="btn btn-primary">{audience.ctaText} →</Link>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- value math ---------------- */

function ValueMath() {
  return (
    <Reveal>
      <div className="card value-math">
        <h3>Do the math on your first win</h3>
        <p>
          A single <strong>$10,000</strong> award covers roughly <strong>four years</strong> of Growth ($2,499/yr),
          or <strong>two years</strong> of Professional ($4,899/yr). A <strong>$2,500</strong> scholarship covers more
          than <strong>six years</strong> of Individual ($399/yr).
        </p>
        <p className="mkt-micro">Grants are competitive — nothing here is guaranteed. But the upside of one win dwarfs the cost of looking.</p>
      </div>
    </Reveal>
  );
}

/* ---------------- scam safety ---------------- */

const SCAM_POINTS = [
  {
    title: 'The government never calls about grants',
    text: 'No calls, texts, or DMs offering you grant money. Real grants require a written application — you find them, never the other way around.',
  },
  {
    title: 'Real aid is always free to apply for',
    text: 'Anyone demanding an upfront \u201cprocessing fee\u201d — especially by gift card, wire, or crypto — is a scammer. Walk away.',
  },
  {
    title: 'Our promise to you',
    text: 'Navigator will never ask you for fees to \u201cunlock\u201d funding. Your subscription is the only thing you ever pay us.',
  },
];

function ScamSafety() {
  return (
    <section className="mkt-section" id="scam-safe" aria-labelledby="scam-safe-h">
      <div className="container">
        <Reveal>
          <span className="lp-kicker">Stay safe</span>
          <h2 className="mkt-h2" id="scam-safe-h">A quick word on scams — because we want you safe</h2>
          <p className="mkt-sub">
            If you’re new to grants, know this: scammers love beginners. Here’s how to spot them,
            straight from the Federal Trade Commission.
          </p>
        </Reveal>
        <div className="mkt-grid-3">
          {SCAM_POINTS.map((s, i) => (
            <Reveal key={s.title} delay={i === 1 ? 'reveal-d1' : i === 2 ? 'reveal-d2' : ''}>
              <div className="card mkt-feat">
                <div className="mkt-feat-ico" aria-hidden="true"><ShieldCheckIcon size={22} /></div>
                <h3>{s.title}</h3>
                <p>{s.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal>
          <p className="scam-report">
            If someone contacts you about grant money, report it at{' '}
            <a href="https://reportfraud.ftc.gov" target="_blank" rel="noopener noreferrer">ReportFraud.ftc.gov</a>.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- feature sections ---------------- */

interface Feature {
  key: string;
  img: string;
  alt: string;
  kicker: string;
  title: string;
  body: string;
  bullets: string[];
  link: string;
  linkText: string;
}

const FEATURES: Feature[] = [
  {
    key: 'readiness',
    img: '/media/readiness-team.webp',
    alt: 'Diverse nonprofit team reviewing a printed readiness checklist around a wooden table',
    kicker: 'Readiness',
    title: 'Know you’re grant-ready before you apply',
    body: 'Readiness checklists turn eligibility into software. Nonprofit and contractor tracks walk you through registrations, financials, and documentation — so gaps surface weeks before a deadline, not the night before.',
    bullets: [
      'Nonprofit and contractor tracks tailored to how you compete',
      'Registration and certification tracking in one place',
      'Every checklist item tied to an owner and a due date',
    ],
    link: '/app/readiness',
    linkText: 'Explore readiness',
  },
  {
    key: 'feeds',
    img: '/media/federal-feeds-desk.webp',
    alt: 'Grant professional researching opportunities on a laptop at a desk with a corkboard of pinned notices',
    kicker: 'Federal data feeds',
    title: 'Federal feeds, filtered to what actually fits',
    body: 'Search and filter grants and RFPs across federal, state, and foundation sources. Every result carries a match score explained against your organization’s profile — never just a number — so you can defend your pipeline to the board.',
    bullets: [
      'Search across federal, state, and foundation sources',
      'Match scores explained with cited reasons',
      SET_ASIDE_FILTER_CLAIM,
    ],
    link: '/app/discover',
    linkText: 'Explore Discover',
  },
  {
    key: 'vault',
    img: '/media/document-vault.webp',
    alt: 'Team organizing grant documents into labeled archive boxes on a conference table',
    kicker: 'Document vault',
    title: 'Every attachment, exactly where it belongs',
    body: 'The document vault keeps your organization’s proof in one place — registrations, financials, certifications, and past performance — versioned and easy to find the moment a solicitation asks for it.',
    bullets: [
      'Central library for registrations and certifications',
      'Versioned files so nothing gets overwritten',
      'Organized for proposals, audits, and renewals',
    ],
    link: '/app/vault',
    linkText: 'Explore the vault',
  },
  {
    key: 'gonogo',
    img: '/media/gonogo-meeting.webp',
    alt: 'Leadership team reviewing charts and reports to make a go or no-go funding decision',
    kicker: 'Go/no-go scoring',
    title: 'Make the go/no-go call with evidence',
    body: 'Every opportunity gets an eligibility and readiness breakdown: strong matches, partial matches, missing information, and disqualifying criteria — each with cited sources. Record your go/no-go with a named owner and a decision date.',
    bullets: [
      'Evidence-backed fit briefs with cited sources',
      'Eligibility and readiness scored per opportunity',
      'Go/no-go decisions recorded with owners and dates',
    ],
    link: '/app/discover',
    linkText: 'Explore an opportunity',
  },
  {
    key: 'proposals',
    img: '/media/proposal-draft.webp',
    alt: 'Grant writer drafting a proposal by hand at a desk with a laptop and research notes',
    kicker: 'Proposal workspace',
    title: 'Draft, budget, and review in one workspace',
    body: 'Each pursuit gets a dedicated proposal workspace: narrative sections, compliance checklists, discussion threads, attachments, and version snapshots. A live readiness score shows exactly what’s done — and what’s blocking submission.',
    bullets: [
      'Narrative, compliance, discussion, and attachments in one view',
      'Version snapshots with one-click restore',
      'Readiness score tracks sections, compliance, and owners',
    ],
    link: '/app/proposals',
    linkText: 'Explore proposals',
  },
  {
    key: 'brief',
    img: '/media/morning-brief.webp',
    alt: 'Nonprofit executive starting her morning reading her briefing on a tablet with coffee',
    kicker: 'Morning brief',
    title: 'Start every day knowing what matters',
    body: 'The morning brief opens your overview with what’s new: deadlines approaching, fresh matches from your feeds, and actions waiting on you. One glance replaces the spreadsheet check-in and the inbox scan.',
    bullets: [
      'Deadlines and schedule risk up top',
      'New matches from your federal feeds',
      'Actions assigned to you, surfaced automatically',
    ],
    link: '/app/overview',
    linkText: 'Explore the overview',
  },
];

function FeatureSection({ feature, flip }: { feature: Feature; flip: boolean }) {
  const headingId = `feature-${feature.key}`;
  return (
    <section className={`lp-section${flip ? ' alt' : ''}`} aria-labelledby={headingId}>
      <div className="container lp-feature">
        <Reveal className={`lp-feature-media${flip ? ' flip' : ''}`}>
          <img
            src={feature.img}
            alt={feature.alt}
            loading="lazy"
            decoding="async"
            className="lp-photo"
          />
        </Reveal>
        <Reveal delay="reveal-d1" className="lp-feature-copy">
          <span className="lp-kicker">{feature.kicker}</span>
          <h2 className="mkt-h2" id={headingId}>{feature.title}</h2>
          <p className="lp-body">{feature.body}</p>
          <ul className="lp-bullets">
            {feature.bullets.map((b) => <li key={b}>{b}</li>)}
          </ul>
          <Link to={feature.link} className="lp-explore">
            {feature.linkText} <span aria-hidden="true">→</span>
          </Link>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- trust strip: live platform stats, capability fallback ---------------- */

interface PublicStats {
  opportunitiesTracked: number;
  sourcesMonitored: number;
  addedLast30Days: number;
}

/**
 * Live platform activity from GET /api/v1/public/stats. Returns null until
 * (and unless) the endpoint answers with real, non-zero numbers — the strip
 * then falls back to the capability claims below. We never render invented
 * or zero figures.
 */
function usePublicStats(): PublicStats | null {
  const [stats, setStats] = useState<PublicStats | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetch('/api/v1/public/stats')
      .then((res) => (res.ok ? res.json() : null))
      .then((data: { ok?: boolean; stats?: PublicStats } | null) => {
        if (cancelled || !data?.ok || !data.stats) return;
        const s = data.stats;
        if (
          typeof s.opportunitiesTracked === 'number' && s.opportunitiesTracked > 0 &&
          typeof s.sourcesMonitored === 'number' && s.sourcesMonitored > 0 &&
          typeof s.addedLast30Days === 'number' && s.addedLast30Days > 0
        ) {
          setStats(s);
        }
      })
      .catch(() => {
        // Silent fallback: the capability strip stays.
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return stats;
}

function TrustStrip() {
  const stats = usePublicStats();
  if (stats) {
    return (
      <div className="trust-strip">
        <div className="container trust-strip-inner trust-stats-inner">
          <div className="trust-item"><span className="t-num t-stat">{stats.opportunitiesTracked.toLocaleString()}</span><span className="t-label">opportunities tracked<br />&amp; scored for fit</span></div>
          <div className="trust-item"><span className="t-num t-stat">{stats.sourcesMonitored.toLocaleString()}</span><span className="t-label">funding sources monitored<br />across federal feeds</span></div>
          <div className="trust-item"><span className="t-num t-stat">{stats.addedLast30Days.toLocaleString()}</span><span className="t-label">opportunities added<br />in the last 30 days</span></div>
        </div>
      </div>
    );
  }
  return (
    <div className="trust-strip">
      <div className="container trust-strip-inner">
        <div className="trust-item"><span className="t-num">Fit scoring</span><span className="t-label">every score explained<br />against your org profile</span></div>
        <div className="trust-item"><span className="t-num">Workback plans</span><span className="t-label">deadline risk flagged<br />before it costs you</span></div>
        <div className="trust-item"><span className="t-num">Proposal readiness</span><span className="t-label">owners, narrative, budget,<br />compliance in one view</span></div>
        <div className="trust-item"><span className="t-num">Eligibility checks</span><span className="t-label">know you qualify<br />before you invest weeks</span></div>
      </div>
    </div>
  );
}

/* ---------------- page ---------------- */

export function Landing() {
  return (
    <div className="mkt">
      {/* Header */}
      <header className="mkt-nav">
        <div className="container mkt-nav-inner">
          <Link to="/" className="mkt-brand" aria-label="The Grant and RFP Navigator — home" onClick={scrollToTop}>
            <BrandLogo variant="color" height={48} />
          </Link>
          <nav className="mkt-links" aria-label="Product">
            <a href="#screenshots">Product</a>
            <a href="#how">Solutions</a>
            <a href="/pricing">Pricing</a>
          </nav>
          <div className="mkt-nav-cta">
            <Link to="/login" className="btn btn-ghost">Log in</Link>
            <Link to="/signup" className="btn btn-primary">Get started</Link>
          </div>
        </div>
      </header>

      <main>
      <Hero />

      {/* Trust strip — live platform stats when available, capabilities otherwise */}
      <TrustStrip />

      {/* Beginner explainer + audience paths */}
      <NewHere />
      <AudienceTabs />

      {/* Scam safety — protective framing belongs with beginner education */}
      <ScamSafety />

      {/* How it works */}
      <section className="mkt-section lp-how" id="how">
        <div className="container">
          <Reveal>
            <h2 className="mkt-h2">How it works</h2>
            <p className="mkt-sub">Four steps from signup to a submitted proposal.</p>
          </Reveal>
          <div className="mkt-steps">
            {[
              { title: 'Tell us who you are', text: 'Complete your organization profile — mission, capabilities, NAICS codes, past performance. This powers every match score.' },
              { title: 'Discover your matches', text: `Browse opportunities scored and ranked for fit. ${SET_ASIDE_FILTER_CLAIM}.` },
              { title: 'Decide with evidence', text: 'Get an evidence-backed fit brief, resolve open questions, and record a go/no-go with a named owner and decision date.' },
              { title: 'Deliver the proposal', text: 'Follow your deadline workback plan, draft the narrative, build the budget — and submit through a gated review process.' },
            ].map((s) => (
              <Reveal key={s.title}>
                <div className="mkt-step"><h3>{s.title}</h3><p>{s.text}</p></div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* See it in action — product screenshots */}
      <section className="lp-section" id="screenshots">
        <div className="container">
          <Reveal>
            <h2 className="mkt-h2">See it in action</h2>
            <p className="mkt-sub">A tour of the workspace, captured from the live app.</p>
          </Reveal>
          <div className="lp-shots">
            {SCREENSHOTS.map((shot) => (
              <Reveal key={shot.key}>
                <BrowserFrame shot={shot} wide={shot.key === 'proposals'} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Feature sections with photography */}
      {FEATURES.map((f, i) => (
        <FeatureSection key={f.key} feature={f} flip={i % 2 === 1} />
      ))}

      {/* Pricing */}
      <section className="mkt-section alt" id="pricing">
        <div className="container">
          <Reveal>
            <h2 className="mkt-h2">Pricing that scales with your ambition</h2>
            <p className="mkt-sub">Annual plans. Every plan includes unlimited opportunities, match scoring, and the full pipeline.</p>
          </Reveal>
          <div className="pricing-grid">
            {PLANS.map((p) => (
              <Reveal key={p.name}>
                <div className={`card plan-card${p.featured ? ' featured' : ''}`}>
                  <div className="plan-kicker">{p.kicker}</div>
                  <div className="plan-name">{p.name}</div>
                  <div className="plan-price">{p.price}<span className="plan-per">{p.per}</span></div>
                  <ul className="plan-feats">{p.features.map((f) => <li key={f}>{f}</li>)}</ul>
                  {p.ctaHref.startsWith('mailto:') ? (
                    <a href={p.ctaHref} className={`btn btn-block ${p.featured ? 'btn-primary' : 'btn-secondary'}`}>{p.cta}</a>
                  ) : (
                    <Link to={p.ctaHref} className={`btn btn-block ${p.featured ? 'btn-primary' : 'btn-secondary'}`}>{p.cta}</Link>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
          <ValueMath />
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-section">
        <div className="container">
          <Reveal>
            <div className="mkt-cta">
              <h2>Your next award is already published.</h2>
              <p>Join the teams that find it first — and submit the strongest response.</p>
              <div className="mkt-cta-row" style={{ justifyContent: 'center' }}>
                <Link to="/signup" className="btn btn-lg" style={{ background: 'var(--gold)', borderColor: 'var(--gold)', color: 'var(--navy-deep)', fontWeight: 700 }}>Get started</Link>
                <Link to="/login" className="btn btn-secondary btn-lg" style={{ background: 'rgba(255,255,255,0.1)', borderColor: 'rgba(255,255,255,0.25)', color: '#fff' }}>Log in</Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      </main>
      {/* Footer */}
      <footer className="mkt-footer">
        <div className="container">
          <div className="footer-grid">
            <div className="footer-brand">
              <Link to="/" className="mkt-brand" aria-label="The Grant and RFP Navigator — home" onClick={scrollToTop}><BrandLogo variant="color" height={42} /></Link>
              <p>Funding intelligence for nonprofits, small businesses, and the teams that support them.</p>
            </div>
            <nav aria-label="Product"><h4>Product</h4><a href="/app/discover">Discover</a><a href="/app/pipeline">Pipeline</a><a href="/pricing">Pricing</a></nav>
            <nav aria-label="Company"><h4>Company</h4><a href="#how">How it works</a><a href="/login">Log in</a></nav>
            <nav aria-label="Legal"><h4>Legal</h4><a href="/privacy">Privacy</a><a href="/terms">Terms</a></nav>
            <div className="footer-contact">
              <h4>Support</h4>
              <address>
                2600 K Avenue<br />
                Suite 150 B<br />
                Plano, TX 75074<br />
                <a href="mailto:support@grantrfpnavigator.com">support@grantrfpnavigator.com</a><br />
                <a href="tel:+19455440754">(945) 544-0754</a>
              </address>
            </div>
          </div>
          <div className="footer-base">
            <span>© 2026 Grant &amp; RFP Navigator. All rights reserved.</span>
            <span>Built for the teams chasing the next award.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Shared marketing nav for Pricing / Privacy / Terms. */
export function MarketingNav() {
  return (
    <header className="mkt-nav">
      <div className="container mkt-nav-inner">
        <Link to="/" className="mkt-brand" aria-label="The Grant and RFP Navigator — home" onClick={scrollToTop}>
          <BrandLogo variant="color" height={48} />
        </Link>
        <nav className="mkt-links" aria-label="Product">
          <Link to="/#screenshots">Product</Link>
          <Link to="/#how">Solutions</Link>
          <Link to="/pricing">Pricing</Link>
        </nav>
        <div className="mkt-nav-cta">
          <Link to="/login" className="btn btn-ghost">Log in</Link>
          <Link to="/signup" className="btn btn-primary">Get started</Link>
        </div>
      </div>
    </header>
  );
}

/** Shared marketing footer for Pricing / Privacy / Terms. */
export function MarketingFooter() {
  return (
    <footer className="mkt-footer">
      <div className="container">
        <div className="footer-grid">
          <div className="footer-brand">
            <Link to="/" className="mkt-brand" aria-label="The Grant and RFP Navigator — home" onClick={scrollToTop}><BrandLogo variant="color" height={42} /></Link>
            <p>Funding intelligence for nonprofits, small businesses, and the teams that support them.</p>
          </div>
          <nav aria-label="Product"><h4>Product</h4><Link to="/app/discover">Discover</Link><Link to="/app/pipeline">Pipeline</Link><Link to="/pricing">Pricing</Link></nav>
          <nav aria-label="Company"><h4>Company</h4><Link to="/#how">How it works</Link><Link to="/login">Log in</Link></nav>
          <nav aria-label="Legal"><h4>Legal</h4><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link></nav>
          <div className="footer-contact">
            <h4>Support</h4>
            <address>
              2600 K Avenue<br />
              Suite 150 B<br />
              Plano, TX 75074<br />
              <a href="mailto:support@grantrfpnavigator.com">support@grantrfpnavigator.com</a><br />
              <a href="tel:+19455440754">(945) 544-0754</a>
            </address>
          </div>
        </div>
        <div className="footer-base">
          <span>© 2026 Grant &amp; RFP Navigator. All rights reserved.</span>
          <span>Built for the teams chasing the next award.</span>
        </div>
      </div>
    </footer>
  );
}
